<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      @click.self="closeModal"
    >
      <div
        class="bg-white rounded-lg shadow-xl w-full flex flex-col max-w-[90vw] h-[90vh]"
        role="dialog"
        aria-modal="true"
      >
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <h3 class="text-lg font-semibold text-gray-900">{{ modalTitle }}</h3>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600"
            @click="closeModal"
          >
            <font-awesome-icon icon="xmark" class="text-xl" />
          </button>
        </div>

        <div class="px-6 py-4 overflow-auto flex-1">
          <form class="grid gap-6 h-full" style="grid-template-columns: 25% 1fr;" @submit.prevent="handleSubmit">
            <!-- Left Column: Configuration Options -->
            <div class="space-y-6">
              <!-- Name -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  v-model="formData.name"
                  placeholder="Configuration name"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-blue-dark focus:ring-blue-dark"
                />
              </div>

              <!-- Entry Agent -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Entry Agent</label>
                <select
                  v-model="formData.entry_agent"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                >
                  <option value="" disabled>Select an agent...</option>
                  <option
                    v-for="agent in agents"
                    :key="agent.id"
                    :value="agent.id"
                  >
                    {{ agent.name }}
                  </option>
                </select>
                <p class="text-xs text-gray-500 mt-1">The first agent that will handle the call</p>
              </div>

              <!-- Voice & Temperature Row -->
              <div class="grid grid-cols-2 gap-4">
                <!-- Voice -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Voice</label>
                  <select
                    v-model="formData.voice"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                  >
                    <option
                      v-for="voice in (formData.use_external_tts ? CARTESIA_VOICES : OPENAI_VOICES)"
                      :key="voice"
                      :value="voice"
                    >
                      {{ voice.charAt(0).toUpperCase() + voice.slice(1) }}
                    </option>
                  </select>
                </div>

                <!-- Temperature -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Temperature ({{ formData.temperature }})
                  </label>
                  <input
                    type="range"
                    v-model.number="formData.temperature"
                    :min="0"
                    :max="1"
                    :step="0.1"
                    class="w-full accent-orange-500"
                  />
                </div>
              </div>

              <!-- Speed & Emotion Row -->
              <div class="grid grid-cols-2 gap-4">
                <!-- Speed (Always visible - works for both OpenAI and Cartesia) -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Speed ({{ formData.speed }})
                  </label>
                  <input
                    type="range"
                    v-model.number="formData.speed"
                    :min="0.6"
                    :max="1.5"
                    :step="0.1"
                    class="w-full accent-orange-500"
                  />
                  <p class="text-xs text-gray-500 mt-1">0.6 (slowest) to 1.5 (fastest)</p>
                </div>

                <!-- Emotion (Cartesia only) -->
                <div v-if="formData.use_external_tts">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Emotion</label>
                  <select
                    v-model="formData.emotion"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                  >
                    <option value="neutral">Neutral</option>
                    <option value="excited">Excited</option>
                    <option value="content">Content</option>
                    <option value="happy">Happy</option>
                    <option value="curious">Curious</option>
                  </select>
                </div>
              </div>

              <!-- Use External TTS -->
              <div>
                <label class="flex items-center cursor-pointer gap-2">
                  <div class="relative flex items-center">
                    <input
                      type="checkbox"
                      v-model="formData.use_external_tts"
                      class="peer shrink-0 appearance-none w-4 h-4 border-2 border-gray-300 rounded bg-white checked:bg-primary-500 checked:border-orange-500 focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-0"
                    />
                    <font-awesome-icon
                      icon="check"
                      class="absolute w-4 h-4 text-xs hidden peer-checked:block pointer-events-none text-white"
                    />
                  </div>
                  <span class="text-sm text-gray-700">Use External TTS (Cartesia)</span>
                </label>
                <p class="mt-1 ml-6 text-xs text-gray-500">
                  When disabled, uses OpenAI Realtime audio-to-audio
                </p>
              </div>

              <!-- Turn Detection Settings -->
              <div class="border-t pt-4">
                <h4 class="heading-subsection mb-3">Turn Detection</h4>

                <div class="space-y-4">
                  <!-- Type & Eagerness Row -->
                  <div class="grid grid-cols-2 gap-4">
                    <!-- Type -->
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        v-model="formData.turn_detection_type"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                      >
                        <option value="server_vad">Server VAD</option>
                        <option value="semantic_vad">Semantic VAD</option>
                      </select>
                    </div>

                    <!-- Eagerness -->
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Eagerness</label>
                      <select
                        v-model="formData.turn_detection_eagerness"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>

                  <!-- Checkboxes -->
                  <div class="flex gap-6">
                    <label class="flex items-center cursor-pointer gap-2">
                      <div class="relative flex items-center">
                        <input
                          type="checkbox"
                          v-model="formData.turn_detection_interrupt"
                          class="peer shrink-0 appearance-none w-4 h-4 border-2 border-gray-300 rounded bg-white checked:bg-primary-500 checked:border-orange-500 focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-0"
                        />
                        <font-awesome-icon
                          icon="check"
                          class="absolute w-4 h-4 text-xs hidden peer-checked:block pointer-events-none text-white"
                        />
                      </div>
                      <span class="text-sm text-gray-700">Allow Interrupt</span>
                    </label>
                    <label class="flex items-center cursor-pointer gap-2">
                      <div class="relative flex items-center">
                        <input
                          type="checkbox"
                          v-model="formData.turn_detection_create_response"
                          class="peer shrink-0 appearance-none w-4 h-4 border-2 border-gray-300 rounded bg-white checked:bg-primary-500 checked:border-orange-500 focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-0"
                        />
                        <font-awesome-icon
                          icon="check"
                          class="absolute w-4 h-4 text-xs hidden peer-checked:block pointer-events-none text-white"
                        />
                      </div>
                      <span class="text-sm text-gray-700">Create Response</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Status -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  v-model="formData.status"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <!-- Data Elements -->
              <div class="border-t pt-4">
                <h4 class="heading-subsection mb-3">Data Elements</h4>

                <!-- Required Elements -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Required</label>
                  <div class="space-y-2">
                    <div
                      v-for="(elem, index) in formData.data_elements.required"
                      :key="'req-' + index"
                      class="flex gap-2 items-center"
                    >
                      <input
                        type="text"
                        :value="elem"
                        disabled
                        :class="[
                          'flex-1 px-2 py-1 border rounded-md text-sm',
                          isLockedField(elem) ? 'bg-gray-100 border-gray-200' : 'bg-gray-50 border-gray-300'
                        ]"
                      />
                      <button
                        v-if="!isLockedField(elem)"
                        type="button"
                        class="text-red-600 hover:text-red-800 p-1"
                        @click="removeDataElement('required', index)"
                      >
                        <font-awesome-icon icon="xmark" />
                      </button>
                      <div v-else class="w-6 h-6 flex items-center justify-center">
                        <font-awesome-icon icon="lock" class="text-gray-400 text-xs" />
                      </div>
                    </div>
                    <div class="flex gap-2">
                      <input
                        v-model="newRequiredElement"
                        type="text"
                        placeholder="Add required element"
                        class="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                        @keyup.enter="addDataElement('required')"
                      />
                      <button
                        type="button"
                        class="btn-primary-sm"
                        @click="addDataElement('required')"
                      >
                        <font-awesome-icon icon="plus" />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Optional Elements -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Optional</label>
                  <div class="space-y-2">
                    <div
                      v-for="(elem, index) in formData.data_elements.optional"
                      :key="'opt-' + index"
                      class="flex gap-2 items-center"
                    >
                      <input
                        type="text"
                        :value="elem"
                        disabled
                        class="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm bg-gray-50"
                      />
                      <button
                        type="button"
                        class="text-red-600 hover:text-red-800 p-1"
                        @click="removeDataElement('optional', index)"
                      >
                        <font-awesome-icon icon="xmark" />
                      </button>
                    </div>
                    <div class="flex gap-2">
                      <input
                        v-model="newOptionalElement"
                        type="text"
                        placeholder="Add optional element"
                        class="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark"
                        @keyup.enter="addDataElement('optional')"
                      />
                      <button
                        type="button"
                        class="btn-primary-sm"
                        @click="addDataElement('optional')"
                      >
                        <font-awesome-icon icon="plus" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column: Summary Schema -->
            <div class="flex flex-col">
              <div class="flex items-center justify-between mb-1">
                <label class="block text-sm font-medium text-gray-700">Summary Schema (JSON)</label>
                <button
                  type="button"
                  class="text-xs text-orange-600 hover:text-orange-700 font-medium"
                  @click="loadDefaultSummarySchema"
                >
                  Load Default
                </button>
              </div>
              <p class="text-xs text-gray-500 mb-2">
                Define the structure for AI to populate after each call
              </p>
              <textarea
                v-model="summarySchemaJson"
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-blue-dark resize-none flex-1 font-mono text-xs"
                placeholder="Define the JSON schema for AI-generated call summaries..."
                :class="{ 'border-red-500': summarySchemaError }"
              />
              <p v-if="summarySchemaError" class="text-xs text-red-600 mt-1">
                {{ summarySchemaError }}
              </p>
            </div>
          </form>
        </div>

        <div class="px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0">
          <div class="flex justify-between gap-3">
            <div>
              <span :class="['px-3 py-1.5 text-xs font-semibold rounded-full border', getStatusColorClass(formData.status)]">
                {{ formData.status.charAt(0).toUpperCase() + formData.status.slice(1) }}
              </span>
            </div>
            <div class="flex gap-3">
              <button
                type="button"
                class="font-semibold rounded-md transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-200 hover:bg-gray-300 text-gray-800 h-8 px-3 py-2 text-sm"
                @click="closeModal"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn-primary-sm"
                :disabled="isSaving"
                @click="handleSubmit"
              >
                <font-awesome-icon v-if="isSaving" icon="spinner" spin />
                {{ config ? 'Update' : 'Create' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
const BADGE_COLORS = {
  success: 'bg-green-100 text-green-800 border-green-300',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  neutral: 'bg-gray-100 text-gray-800 border-gray-300',
}

const DEFAULT_CONFIG = {
  name: '',
  entry_agent: '',
  temperature: 0.8,
  voice: 'katie',
  use_external_tts: true,
  speed: 1.0,
  emotion: 'neutral',
  turn_detection_type: 'semantic_vad',
  turn_detection_eagerness: 'high',
  turn_detection_interrupt: true,
  turn_detection_create_response: true,
  data_elements: {
    required: ['bene_first_name', 'bene_last_name', 'bene_dob', 'patient_identifier'],
    optional: []
  },
  summary_schema: {},
  status: 'draft',
}

const LOCKED_REQUIRED_FIELDS = ['bene_first_name', 'bene_last_name', 'bene_dob', 'patient_identifier']

const DEFAULT_SUMMARY_SCHEMA = {
  type: 'object',
  properties: {
    call_outcome: {
      type: 'string',
      description: 'Overall outcome of the call',
      enum: [
        'appointment_scheduled',
        'callback_requested',
        'declined',
        'no_answer',
        'left_voicemail',
        'wrong_number',
        'other'
      ]
    },
    key_points: {
      type: 'array',
      description: 'Main topics or concerns discussed during the call',
      items: { type: 'string' }
    },
    action_items: {
      type: 'array',
      description: 'Any follow-up actions identified during the call',
      items: { type: 'string' }
    },
    patient_sentiment: {
      type: 'string',
      description: "Patient's overall sentiment during the call",
      enum: ['positive', 'neutral', 'negative', 'concerned', 'unable_to_assess']
    },
    additional_notes: {
      type: 'string',
      description: 'Any other relevant information from the call'
    }
  },
  required: ['call_outcome', 'key_points', 'action_items', 'patient_sentiment', 'additional_notes'],
  additionalProperties: false
}

const CARTESIA_VOICES = ['katie', 'jacqueline', 'brooke', 'kiefer', 'brandon', "cory", "tessa", "daniela"]
const OPENAI_VOICES = ['marin', 'ash', 'sage']

export default {
  name: 'CallConfigurationModal',
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    config: {
      type: Object,
      default: null,
    },
    agents: {
      type: Array,
      required: true,
    },
  },
  emits: ['update:modelValue', 'save'],
  data() {
    return {
      isSaving: false,
      originalData: null,
      formData: { ...DEFAULT_CONFIG },
      newRequiredElement: '',
      newOptionalElement: '',
      summarySchemaJson: '',
      summarySchemaError: '',
      CARTESIA_VOICES,
      OPENAI_VOICES,
      LOCKED_REQUIRED_FIELDS,
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    modalTitle() {
      if (!this.config) return 'New Configuration'
      return this.formData.name || 'Edit Configuration'
    },
  },
  watch: {
    'formData.use_external_tts'(useExternal) {
      const validVoices = useExternal ? CARTESIA_VOICES : OPENAI_VOICES
      if (!validVoices.includes(this.formData.voice)) {
        this.formData.voice = validVoices[0] || 'katie'
      }
    },
    config: {
      handler(newConfig) {
        if (newConfig) {
          const requiredElements = newConfig.data_elements?.required || []
          const mergedRequired = [...new Set([...LOCKED_REQUIRED_FIELDS, ...requiredElements])]

          this.formData = {
            id: String(newConfig.id || ''),
            name: String(newConfig.name || ''),
            entry_agent: String(newConfig.entry_agent || ''),
            temperature: Number(newConfig.temperature || 0.8),
            voice: String(newConfig.voice || 'katie'),
            use_external_tts: Boolean(newConfig.use_external_tts ?? true),
            speed: Number(newConfig.speed || 1.0),
            emotion: String(newConfig.emotion || 'neutral'),
            turn_detection_type: String(newConfig.turn_detection_type || 'semantic_vad'),
            turn_detection_eagerness: String(newConfig.turn_detection_eagerness || 'high'),
            turn_detection_interrupt: Boolean(newConfig.turn_detection_interrupt),
            turn_detection_create_response: Boolean(newConfig.turn_detection_create_response),
            data_elements: {
              required: mergedRequired,
              optional: newConfig.data_elements?.optional || [],
            },
            summary_schema: newConfig.summary_schema || DEFAULT_SUMMARY_SCHEMA,
            status: String(newConfig.status || 'draft'),
          }
          this.summarySchemaJson = JSON.stringify(this.formData.summary_schema, null, 2)
          this.originalData = { ...this.formData }
        } else {
          this.formData = { 
            ...DEFAULT_CONFIG, 
            data_elements: { required: [...LOCKED_REQUIRED_FIELDS], optional: [] },
            summary_schema: { ...DEFAULT_SUMMARY_SCHEMA }
          }
          this.summarySchemaJson = JSON.stringify(DEFAULT_SUMMARY_SCHEMA, null, 2)
          this.originalData = { ...this.formData }
        }
      },
      immediate: true,
    },
  },
  methods: {
    closeModal() {
      if (this.originalData) {
        this.formData = { ...this.originalData }
      }
      this.isOpen = false
    },
    async handleSubmit() {
      this.summarySchemaError = ''
      try {
        const parsedSchema = JSON.parse(this.summarySchemaJson)
        this.formData.summary_schema = parsedSchema
      } catch (e) {
        this.summarySchemaError = 'Invalid JSON format'
        return
      }

      this.isSaving = true
      try {
        this.$emit('save', this.formData)
      } finally {
        this.isSaving = false
      }
    },
    loadDefaultSummarySchema() {
      this.summarySchemaJson = JSON.stringify(DEFAULT_SUMMARY_SCHEMA, null, 2)
      this.summarySchemaError = ''
    },
    addDataElement(type) {
      const value = type === 'required' ? this.newRequiredElement : this.newOptionalElement
      if (!value.trim()) return

      if (!this.formData.data_elements[type].includes(value.trim())) {
        this.formData.data_elements[type].push(value.trim())
      }

      if (type === 'required') {
        this.newRequiredElement = ''
      } else {
        this.newOptionalElement = ''
      }
    },
    removeDataElement(type, index) {
      const element = this.formData.data_elements[type][index]
      if (type === 'required' && LOCKED_REQUIRED_FIELDS.includes(element)) {
        return
      }
      this.formData.data_elements[type].splice(index, 1)
    },
    isLockedField(fieldName) {
      return LOCKED_REQUIRED_FIELDS.includes(fieldName)
    },
    getStatusColorClass(status) {
      const statusLower = (status || '').toLowerCase()
      const configColors = {
        published: BADGE_COLORS.success,
        draft: BADGE_COLORS.warning,
        archived: BADGE_COLORS.neutral,
      }
      return configColors[statusLower] || BADGE_COLORS.neutral
    },
  },
}
</script>
